import { useEffect, useRef, useState } from 'react'

const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3)

/**
 * Animates a number from its previous value to `target` on change.
 * Skips straight to `target` when `disabled` (e.g. prefers-reduced-motion).
 */
export const useCountUp = (
  target: number,
  duration = 900,
  disabled = false
) => {
  const safeTarget = Number.isFinite(target) ? target : 0
  const [value, setValue] = useState(disabled ? safeTarget : 0)
  const fromRef = useRef(0)
  const frameRef = useRef<number>()

  useEffect(() => {
    if (disabled) {
      fromRef.current = safeTarget
      setValue(safeTarget)
      return
    }

    const from = fromRef.current
    const startTime = performance.now()

    const tick = (now: number) => {
      const progress = Math.min((now - startTime) / duration, 1)
      setValue(from + (safeTarget - from) * easeOutCubic(progress))
      if (progress < 1) {
        frameRef.current = requestAnimationFrame(tick)
      } else {
        fromRef.current = safeTarget
      }
    }

    frameRef.current = requestAnimationFrame(tick)
    return () => {
      if (frameRef.current) cancelAnimationFrame(frameRef.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [safeTarget, duration, disabled])

  return value
}
